/**
 * firehoseRetry — bounded retry for the first-launch HealthKit auth race (G2).
 *
 * The cold first healthd query can return empty (auth still .notDetermined) or
 * exceed the 5s bridge timeout, which used to blank the Swim tab permanently.
 * This retries the fetch a few times with backoff so a real swim survives the
 * auth-resolution window. Pure + injectable sleep so it is unit-tested.
 */
import type { SwimFirehoseResponse } from "@contract/types";
import { BridgeUnavailableError } from "../bridge/client";

const DEFAULT_BACKOFFS_MS = [800, 1600, 3200];

export interface FirehoseRetryOptions {
  backoffsMs?: number[];
  sleep?: (ms: number) => Promise<void>;
  isCancelled?: () => boolean;
}

export async function fetchFirehoseWithRetry(
  fetchOnce: () => Promise<SwimFirehoseResponse | null>,
  hasExplicitUUID: boolean,
  opts: FirehoseRetryOptions = {},
): Promise<{ result: SwimFirehoseResponse | null; error: unknown }> {
  const backoffs = opts.backoffsMs ?? DEFAULT_BACKOFFS_MS;
  const sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  const cancelled = opts.isCancelled ?? (() => false);

  let lastError: unknown = null;
  for (let attempt = 0; attempt <= backoffs.length; attempt++) {
    try {
      const res = await fetchOnce();
      lastError = null;
      if (res) return { result: res, error: null };
      // A null result for an explicit swim UUID means "gone", not the auth race.
      if (hasExplicitUUID) return { result: null, error: null };
    } catch (err) {
      lastError = err;
      // A missing bridge (desktop preview) will never resolve — do not spin.
      if (err instanceof BridgeUnavailableError && err.reason === "no-handler") {
        return { result: null, error: err };
      }
    }
    if (cancelled()) return { result: null, error: lastError };
    const backoff = backoffs[attempt];
    if (backoff === undefined) break; // retries exhausted
    await sleep(backoff);
    if (cancelled()) return { result: null, error: lastError };
  }
  return { result: null, error: lastError };
}
